import dayjs, { type ConfigType, type Dayjs } from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { DEFAULT_APP_SETTINGS, type AppSettings } from '@/global/globalState';

/**
 * Display formatting driven by the school's Global Settings (see
 * `GlobalState.appSettings`, loaded by `useAppSettingsSync`). Wire formats sent
 * to the API (`YYYY-MM-DD`) are not display formatting and must not go through here.
 *
 * The module-level functions read the settings currently configured by
 * `GlobalStateProvider`, so module-level column definitions can use them
 * directly. The app shell remounts page content when settings change.
 */

dayjs.extend(updateLocale);

let settings: AppSettings = DEFAULT_APP_SETTINGS;
let currencyFormatter = createCurrencyFormatter(settings);

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 0 (Sunday) .. 6 (Saturday), for grids that lay out a week. */
export const weekStartIndex = (s: AppSettings = settings) => Math.max(0, WEEKDAYS.indexOf(s.firstDayOfWeek));

/** Everything that changes how values are written; the version alone changes nothing on screen. */
export const formattingKey = (s: AppSettings) =>
  (Object.keys(DEFAULT_APP_SETTINGS) as (keyof AppSettings)[])
    .filter((k) => k !== 'version')
    .map((k) => String(s[k]))
    .join('|');

function createCurrencyFormatter(s: AppSettings): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(s.locale, {
      style: 'currency',
      currency: s.currency,
      maximumFractionDigits: 0,
    });
  } catch {
    // An invalid locale or currency code must not take the page down.
    return new Intl.NumberFormat(DEFAULT_APP_SETTINGS.locale, {
      style: 'currency',
      currency: DEFAULT_APP_SETTINGS.currency,
      maximumFractionDigits: 0,
    });
  }
}

/**
 * Applies app settings to the shared formatters and to dayjs's week start (used
 * by the date pickers). Called by `GlobalStateProvider`. Missing fields take the
 * defaults.
 */
export function configureFormatting(next: Partial<AppSettings>): void {
  const merged: AppSettings = { ...DEFAULT_APP_SETTINGS, ...next };
  if (formattingKey(merged) === formattingKey(settings)) return;
  const weekStartChanged = merged.firstDayOfWeek !== settings.firstDayOfWeek;
  settings = merged;
  currencyFormatter = createCurrencyFormatter(merged);
  if (weekStartChanged) dayjs.updateLocale('en', { weekStart: weekStartIndex(merged) });
}

/** Whole-unit currency amount, e.g. `₹21,000`. */
export function formatCurrency(amount: number | bigint): string {
  return currencyFormatter.format(amount);
}

/** Short currency amount for chart axes, e.g. `₹30L` or `$1.2M`, in the configured locale. */
export function formatCurrencyCompact(amount: number): string {
  const { locale, currency: code } = currencyFormatter.resolvedOptions();
  return new Intl.NumberFormat(locale, { style: 'currency', currency: code, notation: 'compact', maximumFractionDigits: 1 }).format(amount);
}

/** Drop-in for the per-page `new Intl.NumberFormat(...)` instances: `currency.format(n)`. */
export const currency = { format: formatCurrency } as const;

/**
 * Symbol of the configured currency as the configured locale writes it,
 * e.g. `₹`, `$`, `£`. Falls back to the ISO code when there is no symbol.
 */
export function currencySymbol(): string {
  return (
    currencyFormatter.formatToParts(0).find((p) => p.type === 'currency')?.value ??
    activeCurrencyCode()
  );
}

/**
 * Symbol and English name of any ISO 4217 code as `locale` writes it, e.g.
 * `{ symbol: '₹', name: 'Indian Rupee' }` for INR. Unknown codes fall back to the code.
 */
export function describeCurrency(code: string, locale: string = settings.locale): { symbol: string; name: string } {
  let symbol = code;
  let name = code;
  try {
    symbol = new Intl.NumberFormat(locale, { style: 'currency', currency: code })
      .formatToParts(0).find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    // An unsupported locale or code keeps the code.
  }
  try {
    name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code;
  } catch {
    // Intl.DisplayNames is missing in very old browsers.
  }
  return { symbol, name };
}

/** A form label with the currency symbol appended, e.g. `Amount (₹)`. */
export function currencyLabel(label: string): string {
  return `${label} (${currencySymbol()})`;
}

/** Minor-unit names for the currencies a school is likely to bill in; anything else uses "Cents". */
const SUBUNIT_NAMES: Record<string, string> = {
  INR: 'Paise',
  GBP: 'Pence',
  AED: 'Fils',
  KWD: 'Fils',
  BHD: 'Fils',
  OMR: 'Baisa',
  QAR: 'Dirhams',
  SAR: 'Halalas',
};

/** The configured currency code, or the default when it isn't a valid ISO code. */
function activeCurrencyCode(): string {
  return currencyFormatter.resolvedOptions().currency ?? DEFAULT_APP_SETTINGS.currency;
}

/** Plural English name of the configured currency, e.g. `Indian Rupees`, `US Dollars`. */
export function currencyName(): string {
  const code = activeCurrencyCode();
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code);
    if (!name || name === code) return code;
    const titled = name.replace(/\b\w/g, (c) => c.toUpperCase());
    return titled.endsWith('s') ? titled : `${titled}s`;
  } catch {
    return code;
  }
}

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** Groups from largest to smallest; INR uses the Indian lakh/crore system. */
const INDIAN_GROUPS: [number, string][] = [
  [10_000_000, 'Crore'],
  [100_000, 'Lakh'],
  [1000, 'Thousand'],
];
const WESTERN_GROUPS: [number, string][] = [
  [1_000_000_000, 'Billion'],
  [1_000_000, 'Million'],
  [1000, 'Thousand'],
];

function wholeToWords(n: number, groups: [number, string][]): string {
  if (n < 20) return ONES[n] ?? '';
  if (n < 100) return [TENS[Math.floor(n / 10)], ONES[n % 10]].filter(Boolean).join(' ');
  if (n < 1000) {
    return [`${ONES[Math.floor(n / 100)]} Hundred`, wholeToWords(n % 100, groups)]
      .filter(Boolean)
      .join(' ');
  }
  for (const [size, name] of groups) {
    if (n >= size) {
      const head = `${wholeToWords(Math.floor(n / size), groups)} ${name}`;
      return [head, wholeToWords(n % size, groups)].filter(Boolean).join(' ');
    }
  }
  return '';
}

/**
 * An amount written out in the configured currency, for receipts and
 * payslips, e.g. `Twelve Thousand Five Hundred Indian Rupees Only`.
 * Uses lakh/crore grouping for INR and thousand/million/billion otherwise.
 * The sign is ignored, and a missing or non-numeric amount reads as zero.
 */
export function amountInWords(amount: number | string | null | undefined): string {
  const parsed = typeof amount === 'string' ? Number(amount.replace(/,/g, '')) : (amount ?? 0);
  const minor = Number.isFinite(parsed) ? Math.round(Math.abs(parsed) * 100) : 0;
  const units = Math.floor(minor / 100);
  const fraction = minor % 100;
  const code = activeCurrencyCode();
  const groups = code === 'INR' ? INDIAN_GROUPS : WESTERN_GROUPS;
  const name = currencyName();

  if (units === 0 && fraction === 0) return `Zero ${name} Only`;

  const parts: string[] = [];
  if (units > 0) parts.push(`${wholeToWords(units, groups)} ${name}`);
  if (fraction > 0)
    parts.push(`${wholeToWords(fraction, groups)} ${SUBUNIT_NAMES[code] ?? 'Cents'}`);
  return `${parts.join(' and ')} Only`;
}

// ── Time zone ────────────────────────────────────────────────────────────────

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat | null {
  let formatter = zoneFormatters.get(timeZone);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      });
    } catch {
      return null; // An unknown zone shows the browser's local time.
    }
    zoneFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * The value as a wall-clock time in `timeZone`. Only moments move: a date-only
 * value (`2026-09-12`), a time without an offset, or a Dayjs is taken as it is.
 */
export function inTimeZone(value: ConfigType, timeZone: string = settings.timeZone): Dayjs {
  const plain =
    value == null || dayjs.isDayjs(value) || (typeof value === 'string' && (DATE_ONLY.test(value) || !HAS_OFFSET.test(value)));
  const parsed = dayjs(value);
  if (plain || !parsed.isValid()) return parsed;

  const formatter = zoneFormatter(timeZone);
  if (!formatter) return parsed;
  const parts = Object.fromEntries(formatter.formatToParts(parsed.toDate()).map((p) => [p.type, p.value]));
  return dayjs(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`);
}

// ── Patterns ─────────────────────────────────────────────────────────────────

export const monthPattern = (display: AppSettings['monthDisplay']) =>
  display === 'Full' ? 'MMMM YYYY' : display === 'Short' ? 'MMM YYYY' : 'MM/YYYY';

export function timePattern(s: Pick<AppSettings, 'timeFormat' | 'showSeconds' | 'amPmStyle'>): string {
  const seconds = s.showSeconds ? ':ss' : '';
  if (s.timeFormat === 'TwentyFourHour') return `HH:mm${seconds}`;
  return `hh:mm${seconds} ${s.amPmStyle === 'Upper' ? 'A' : 'a'}`;
}

// ── Numbers ──────────────────────────────────────────────────────────────────

/** `1234567` grouped as `12,34,567` (Indian) or `1,234,567` (International). */
function groupDigits(digits: string, s: Pick<AppSettings, 'numberFormat' | 'thousandSeparator'>): string {
  if (s.numberFormat === 'Indian' && digits.length > 3) {
    const head = digits.slice(0, -3);
    return [...(head.match(/\d{1,2}(?=(\d{2})*$)/g) ?? []), digits.slice(-3)].join(s.thousandSeparator);
  }
  return (digits.match(/\d{1,3}(?=(\d{3})*$)/g) ?? [digits]).join(s.thousandSeparator);
}

export function formatNumberWith(
  s: Pick<AppSettings, 'numberFormat' | 'thousandSeparator' | 'decimalSymbol' | 'decimalPrecision'>,
  value: number,
  decimals = s.decimalPrecision,
): string {
  if (!Number.isFinite(value)) return '—';
  const [whole = '0', fraction = ''] = Math.abs(value).toFixed(decimals).split('.');
  const sign = value < 0 && Number(`${whole}.${fraction || 0}`) !== 0 ? '-' : '';
  return `${sign}${groupDigits(whole, s)}${fraction ? `${s.decimalSymbol}${fraction}` : ''}`;
}

/**
 * A number in the school's number format, e.g. `1,23,456.79`. `decimals`
 * defaults to the configured precision; pass `0` for counts.
 */
export function formatNumber(value: number, decimals?: number): string {
  return formatNumberWith(settings, value, decimals);
}

// ── Dates and times ──────────────────────────────────────────────────────────

/** Display date in the school's format, e.g. `12 Sep 2026`. A moment is shown in the school's time zone. */
export function formatDate(value: ConfigType): string {
  return inTimeZone(value).format(settings.dateFormat);
}

/** Short date, e.g. `12/09/26`. */
export function formatShortDate(value: ConfigType): string {
  return inTimeZone(value).format(settings.shortDateFormat);
}

/** A month label, e.g. `Sep 2026`, as Global Settings › Display Month As says. */
export function formatMonth(value: ConfigType): string {
  return inTimeZone(value).format(monthPattern(settings.monthDisplay));
}

/** Time of day in the school's clock, e.g. `02:30 PM`. */
export function formatTime(value: ConfigType): string {
  return inTimeZone(value).format(timePattern(settings));
}

/** Date and time, e.g. `12 Sep 2026 02:30 PM`, in the school's time zone. */
export function formatDateTime(value: ConfigType): string {
  return inTimeZone(value).format(`${settings.dateFormat} ${timePattern(settings)}`);
}

/** Formatters bound to explicit settings — for components that read settings reactively. */
export function createFormatters(s: AppSettings) {
  const money = createCurrencyFormatter(s);
  const zoned = (value: ConfigType) => inTimeZone(value, s.timeZone);
  return {
    formatCurrency: (amount: number | bigint) => money.format(amount),
    formatNumber: (value: number, decimals?: number) => formatNumberWith(s, value, decimals),
    formatDate: (value: ConfigType) => zoned(value).format(s.dateFormat),
    formatShortDate: (value: ConfigType) => zoned(value).format(s.shortDateFormat),
    formatMonth: (value: ConfigType) => zoned(value).format(monthPattern(s.monthDisplay)),
    formatTime: (value: ConfigType) => zoned(value).format(timePattern(s)),
    formatDateTime: (value: ConfigType) => zoned(value).format(`${s.dateFormat} ${timePattern(s)}`),
  };
}
